import { Card, FilterGroups, FilterValues, PageHeader } from "@cofano/cck";
import { intl } from "LocaleWrapper";
import Helmet from "components/helmet/Helmet";
import { useApolloClient } from "@apollo/client";
import { useStoreState } from "ezStore";
import useGroupedTable from "components/customCompenents/table/useGroupedTable";
import GroupedTable from "components/customCompenents/table/GroupedTable";
import { settingsProvider } from "components/utils/TableUtils";
import {
	OpenDeclarationsDocument,
	OpenDeclarationsQuery,
} from "__generated__/view/declarations/grouped/graphql/queries.generated";
import { DataFormat, hasPluginPermission } from "utils";
import { DossierLink, InvoiceLink, RelationLink, ShipLink } from "utils/LinkUtils";
import StatusChanger from "view/statuses/components/changer/StatusChanger";
import { CharterType, HistoryEntity, SortDirection, StatusType } from "__generated__/enums";
import OverflowTooltip from "components/cck/OverflowTooltip";
import DeclareButton from "view/declarations/components/buttons/DeclareButton";
import EntityHistoryButton from "components/entityHistory/components/EntityHistoryButton";
import { Box, Tooltip } from "@mui/material";
import useOpenCharterSideviewer from "view/charters/components/sideviewer/useOpenCharterSideviewer";
import DeclarationViewButtons, { DeclarationView } from "view/declarations/components/buttons/DeclarationViewButtons";
import useDeclarationFilters from "view/declarations/utils/useDeclarationFilters";
import useDeclarationTable from "view/declarations/utils/useDeclarationTable";
import { FormattedDate } from "utils/DateUtils";
import { EntityHistoryType } from "components/entityHistory/utils/entityHistory.utils";
import { ChildProps, ChildPropsFromPagination } from "types/commons";
import { getInvoiceGroupStatusConfig } from "view/declarations/invoiceGroupStatus";

type DeclarationFromQuery = ChildPropsFromPagination<OpenDeclarationsQuery, "searchDeclarations">;
type CharterFromQuery = ChildProps<DeclarationFromQuery, "charter">;
type InvoiceGroupFromQuery = ChildProps<DeclarationFromQuery, "invoiceGroup">;
type InvoiceLineRow = DeclarationFromQuery["invoiceLines"][number];
type AgreementRow = InvoiceGroupFromQuery["agreements"][number];

type OpenDeclarationRow =
	| ({ rowType: "invoiceLine" } & InvoiceLineRow)
	| ({ rowType: "agreement"; someDeclared: boolean } & AgreementRow);

type PurchaseCharterFromQuery = Extract<CharterFromQuery, { __typename?: "PurchaseCharter" }>;
type SalesCharterFromQuery = Extract<CharterFromQuery, { __typename?: "SalesCharter" }>;

const isPurchaseCharter = (charter: CharterFromQuery): charter is PurchaseCharterFromQuery => "ships" in charter;
const isSalesCharter = (charter: CharterFromQuery): charter is SalesCharterFromQuery => "dossierNumber" in charter;

// A sales charter's ship comes from its voyages; a purchase charter carries a ships list.
const getPrimaryShip = (charter: CharterFromQuery | null | undefined) => {
	if (!charter) {
		return null;
	}
	if (isPurchaseCharter(charter)) {
		return charter.ships?.[0] ?? null;
	}
	return isSalesCharter(charter) ? charter.voyages?.flatMap(v => v?.ship)[0] ?? null : null;
};

const getDossierNumber = (charter: CharterFromQuery | null | undefined): string | null => {
	if (!charter) {
		return null;
	}
	if (isPurchaseCharter(charter)) {
		const numbers = (charter.voyages ?? [])
			.flatMap(v => v?.cargoCharters ?? [])
			.map(sc => sc?.dossierNumber)
			.filter((d): d is string => !!d);
		const unique = Array.from(new Set(numbers));
		return unique.length ? unique.join(", ") : null;
	}
	return isSalesCharter(charter) ? charter.dossierNumber ?? null : null;
};

const getCustomerReference = (charter: CharterFromQuery | null | undefined, charterType: CharterType): string | null => {
	if (!charter) {
		return null;
	}
	if (charterType === CharterType.Purchase && isPurchaseCharter(charter)) {
		const refs = charter.voyages
			?.flatMap(v => v.cargoCharters ?? [])
			.map(sc => sc?.customerReference)
			.filter((r): r is string => !!r) ?? [];

		const unique = Array.from(new Set(refs));
		return unique.length ? unique.join(", ") : null;
	}
	return charter.customerReference ?? null;
};

const GroupedDeclarations = ({ charterType }: {
	charterType: CharterType;
}) => {
	const client = useApolloClient();
	const session = useStoreState((store) => store.session);
	const { locale } = session;
	const { filterFields } = useDeclarationFilters(charterType);
	const { getSearchInput } = useDeclarationTable(charterType);
	const disableInvoiceLineEditing = hasPluginPermission(session, "disableInvoiceLineEditing");
	const openCharterSideviewer = useOpenCharterSideviewer();

	const isSale = charterType === CharterType.Sales;
	const titleKey = isSale ? "ADMINISTRATION_SALE_DECLARATIONS" : "PURCHASE_DECLARATIONS";
	const basePath = isSale ? "/view/declarations/sale" : "/view/declarations/purchase";

	const fields = [
		filterFields.CharterNumber,
		filterFields.ShipName,
		filterFields.CustomerReference,
		filterFields.DossierNumber,
		filterFields.Creditor,
		filterFields.Debtor,
		filterFields.DeclarationStatus,
		filterFields.SalesCharterStatus,
		filterFields.CharterSubtype,
		filterFields.BlDate,
		filterFields.Window,
		filterFields.InvoiceGroupType,
		filterFields.Invoiced,
	];
	const filterValueFields = fields.map(f => ({
		id: f.id,
		title: f.title,
		renderFilterValue: "renderFilterValue" in f ? f.renderFilterValue : undefined,
	}));

	const { tableProps, filterProps, refetch } =
		useGroupedTable<OpenDeclarationRow>({
			id: `${charterType}-declarations`,
			paginationEnabled: true,
			size: "compact",
			settingsProvider: settingsProvider(),
			onChange: async (pagination, filters) => {
				const filter = Object.fromEntries(filters.map(f => [ f.property, f.value ]));

				const result = await client.query({
					query: OpenDeclarationsDocument,
					variables: {
						search: getSearchInput(filter, undefined, charterType),
						pagination: {
							page: pagination.page,
							size: pagination.size,
							// sort by ships, so they're "grouped" by ship
							sortList: [ { field: "ship", direction: SortDirection.Asc } ],
						},
					},
				});

				const declarations = result.data.searchDeclarations.content ?? [];
				const shipGroups = Map.groupBy(declarations, (d) => getPrimaryShip(d.charter)?.id ?? 0);

				return {
					groups: Array.from(shipGroups.entries()).map(([ , declarations ]) => {
						const subGroups = declarations.map((declaration) => {
							const charter = declaration.charter;
							const group = declaration.invoiceGroup;
							const invoiceGroupType = group?.invoiceGroupType ?? declaration.invoiceGroupType;
							const customerReference = getCustomerReference(charter, charterType);
							const dossierNumber = getDossierNumber(charter);

							const declaredAgreementIds = new Set(
								declaration.invoiceLines
									.map((l) => l.agreement?.id)
									.filter(Boolean),
							);
							const missingAgreements = (group?.agreements ?? []).filter(
								(a) => !declaredAgreementIds.has(a.agreement.id),
							);

							const rows: OpenDeclarationRow[] = [
								...declaration.invoiceLines.map((line) => ({
									rowType: "invoiceLine" as const,
									...line,
								})),
								// show the not-yet-declared agreements too, greyed out
								...missingAgreements.map((agreement) => ({
									rowType: "agreement" as const,
									...agreement,
									someDeclared: declaration.invoiceLines.length > 0,
								})),
							];

							return {
								id: declaration.id,
								backgroundColor: "#eee",
								onClick: () => charter && openCharterSideviewer(charter.id, () => refetch()),
								render: () => <InvoiceGroupSubHeader
									declaration={declaration}
									group={group}
									refetch={refetch}
								/>,
								renderCells: () => [
									{
										content: (
											<div
												style={{
													display: "flex",
													alignItems: "center",
													gap: 8,
													width: "100%",
												}}>
												<div style={{ minWidth: 100, flexShrink: 0, whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
													<DossierLink charter={charter} />
												</div>

												<div style={{ width: 140, flexShrink: 0 }}>
													<OverflowTooltip>{invoiceGroupType?.name}</OverflowTooltip>
												</div>

												<div
													style={{
														width: 160,
														flexShrink: 0,
														wordBreak: "break-word",
														whiteSpace: "wrap",
														marginRight: "16px",
													}}
													onClick={(e) => e.stopPropagation()}>
													{group?.debtor && <RelationLink relation={group.debtor} />}
												</div>

												<div style={{ width: 100, flexShrink: 0 }}>
													<OverflowTooltip>{customerReference}</OverflowTooltip>
												</div>

												<div style={{ width: 100, flexShrink: 0 }}>
													<OverflowTooltip>{dossierNumber}</OverflowTooltip>
												</div>

												<div style={{ marginRight: "16px" }} onClick={(e) => e.stopPropagation()}>
													<InvoiceGroupSubHeader
														declaration={declaration}
														group={group}
														refetch={refetch}
														statusOnly
													/>
												</div>

												<div style={{ flexShrink: 0, whiteSpace: "nowrap" }}>
													<FormattedDate date={charter && "blDate" in charter ? charter.blDate : null} format="ll" />
												</div>
											</div>
										),
										colSpan: 3,
									},
									{
										content: (
											<b>{DataFormat.price(declaration.totalPriceExcVAT)}</b>
										),
									},
									{ content: <b>{declaration.vatPercentage}</b> },
									{ content: <b>{DataFormat.price(declaration.totalPriceIncVAT)}</b> },
									{
										content: <div style={{ display: "flex", alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
											{charter && (
												<DeclareButton
													charter={charter}
													invoiceGroupType={invoiceGroupType}
													refetch={refetch}
													declaration={declaration}
												/>
											)}

											<EntityHistoryButton
												entity={HistoryEntity.Declaration}
												entityId={declaration.id}
												type={EntityHistoryType.Absolute}
												maxWidth="md"
											/>
										</div>,
									},
								],
								rows,
							};
						});

						const groupShip = getPrimaryShip(declarations[0].charter);
						return {
							id: groupShip?.id ?? 0,
							backgroundColor: "#D6E4F1",
							render: () => groupShip ? <ShipLink ship={groupShip} /> : <span>{intl.formatMessage({ id: "SHIP" })}</span>,
							subGroups,
						};
					}),
					totalElements: result.data.searchDeclarations.totalElements,
				};
			},
		});

	return <>
		<PageHeader>
			<PageHeader.Title>
				{intl.formatMessage({ id: titleKey })}
				<Helmet title={intl.formatMessage({ id: titleKey })} />
			</PageHeader.Title>
			<PageHeader.Actions>
				<DeclarationViewButtons active={DeclarationView.Grouped} basePath={basePath} />
			</PageHeader.Actions>
		</PageHeader>

		{filterProps.controlledValues.length > 0 && (
			<div style={{ marginBottom: 16 }}>
				<Card disablePadding>
					<FilterValues
						values={filterProps.controlledValues}
						onDelete={(property) => {
							filterProps.onSearch(
								filterProps.controlledValues.filter(
									(v) => v.property !== property,
								),
							);
						}}
						fields={filterValueFields} />
				</Card>
			</div>
		)}

		<div style={{ marginBottom: 16 }}>
			<Card disablePadding>
				<FilterGroups
					{...filterProps}
					defaultOpen={false}
					fields={fields} />
			</Card>
		</div>

		<Card disablePadding>
			<GroupedTable
				{...tableProps}
				getEntityId={(row) => row.rowType === "invoiceLine" ? row.id : row.agreement.id}
				columns={[
					{
						id: "agreement",
						title: intl.formatMessage({ id: "AGREEMENT" }),
						render: (row) => {
							if (row.rowType === "invoiceLine") {
								const color = row.approved ? "#45B741" : "#999999";
								const title = intl.formatMessage({ id: row.approved ? "APPROVED" : "NOT_YET_APPROVED" });

								return <div
									style={{ display: "flex", alignItems: "center", gap: 8 }}
								>
									{!disableInvoiceLineEditing && (
										<Tooltip
											title={title}
											placement="top"
											arrow
										>
											<Box
												component="span"
												sx={{
													display: "inline-block",
													width: 10,
													height: 10,
													borderRadius: "50%",
													bgcolor: color,
													flexShrink: 0,
												}}
											/>
										</Tooltip>
									)}

									{row.description
										? row.description
										: row.agreement?.type
											? intl.formatMessage({ id: `AGREEMENT_TYPE_${row.agreement.type}` })
											: row.costType
												? DataFormat.costType(row.costType, locale)
												: "-"}
								</div>;
							}
							if (row.someDeclared) {
								return (
									<div style={{ color: "#999" }}>
										{intl.formatMessage({ id: `AGREEMENT_TYPE_${row.agreement.type}` })}
									</div>
								);
							}

							const tagConfig = getInvoiceGroupStatusConfig(row);

							return (
								<div
									style={{ display: "flex", alignItems: "center", gap: 8 }}
								>
									<Tooltip
										title={tagConfig.label}
										placement="top"
										arrow
									>
										<Box
											component="span"
											sx={{
												display: "inline-block",
												width: 10,
												height: 10,
												borderRadius: "50%",
												bgcolor: tagConfig.color,
												verticalAlign: "middle",
											}}
										/>
									</Tooltip>
									{intl.formatMessage({
										id: `AGREEMENT_TYPE_${row.agreement.type}`,
									})}
								</div>
							);
						},
					},
					{
						id: "description",
						title: intl.formatMessage({ id: "DESCRIPTION" }),
						render: (row) => {
							if (row.rowType === "agreement" && row.someDeclared) {
								return (
									<span style={{ color: "#999" }}>
										{row.agreement.calculationMethod?.summary}
									</span>
								);
							}
							return row.agreement?.calculationMethod?.summary;
						},
					},
					{
						id: "calculation",
						title: intl.formatMessage({ id: "CALCULATION" }),
						render: (row) =>
							row.rowType === "invoiceLine" && row.calculationSummary,
					},
					{
						id: "totalPriceExcVAT",
						title: intl.formatMessage({ id: "TOTAL_EXCLUDE_VAT" }),
						align: "right" as const,
						render: (row) =>
							row.rowType === "invoiceLine" &&
								DataFormat.price(
									row.totalPriceExcVAT,
									row.price?.currency?.isoCode,
								),
					},
					{
						id: "vatPercentage",
						align: "right" as const,
						title: intl.formatMessage({ id: "VAT" }),
						render: () => <></>,
					},
					{
						id: "totalPriceIncVAT",
						align: "right" as const,
						title: intl.formatMessage({ id: "TOTAL_INCLUDE_VAT" }),
						render: () => <></>,
					},
					{
						id: "actions",
						title: "",
						width: 48,
						render: () => null,
					},
				]}
			/>
		</Card>
	</>;
};

const InvoiceGroupSubHeader = ({
	declaration,
	group,
	refetch,
	statusOnly,
}: {
	declaration: DeclarationFromQuery;
	group: InvoiceGroupFromQuery | null | undefined;
	refetch: () => void;
	statusOnly?: boolean;
}) => {

	const invoice = declaration.invoiceLines.find(l => l.invoice != null)?.invoice;

	const statusContent = (
		<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
			{declaration.lastStatusChange ? (
				<StatusChanger
					statusType={StatusType.Declaration}
					entity={declaration}
					refetch={refetch}
					showHistory
				/>
			) : null}
			{invoice && (
				<Tooltip title={intl.formatMessage({ id: "INVOICE" })}>
					<span><InvoiceLink invoice={invoice} /></span>
				</Tooltip>
			)}
		</div>
	);

	if (statusOnly) {
		return <>{statusContent}</>;
	}

	return (
		<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
			<DossierLink charter={declaration.charter} />
			{(group?.invoiceGroupType ?? declaration.invoiceGroupType)?.name}
			{group?.creditor && (
				<span style={{ color: "#666" }}>
					<RelationLink relation={group.creditor} />
				</span>
			)}
			{group?.debtor && (
				<span style={{ color: "#666" }}>
					<RelationLink relation={group.debtor} />
				</span>
			)}
			{statusContent}
		</div>
	);
};

export default GroupedDeclarations;
